/**
 * FlowOnboardingScreenNodeV2 - Custom ReactFlow node for V2 StepNode screens
 *
 * Architecture:
 * - Receives entityId and basic node data
 * - Uses V2ScreenRegistry to fetch StepNode
 * - Uses V2ScreenView from @segmently/screen-view-v2 for rendering (native V2)
 *
 * Sprint: schema-v2-migration, Sprint 6 (preview integration)
 */
import React, { useRef, useMemo, useCallback } from 'react';
import { NodeProps, NodeToolbar, Position, useStore } from '@xyflow/react';
import { V2ScreenView, AutoScaleScreenContainer, PreviewOptionsRandomizationProvider } from '@modules/screen-view-v2';
import { v2ScreenRegistry } from '@modules/reactFlow/services/onboarding/V2ScreenRegistry';
import { useCanvasLocale } from '@modules/reactFlow/contexts/CanvasLocaleContext';
import { useCanvasVariables } from '@modules/reactFlow/contexts/CanvasVariablesContext';
import { useCanvasBadges } from '@modules/reactFlow/contexts/CanvasBadgesContext';
import { CANVAS_NODE_DIMENSIONS } from '@modules/reactFlow/constants/screenPreviewConfig';
import { ConnectionInputHandle } from '@modules/reactFlow/components/shared/handles/ConnectionInputHandle';
import { ConnectionOutputHandle } from '@modules/reactFlow/components/shared/handles/ConnectionOutputHandle';
import type { NodeData } from '@modules/reactFlow/types/flow-canvas-types';
import { NodeActionToolbar } from '@modules/reactFlow/components/shared/NodeActionToolbar';
import { buildScreenActions } from '@modules/reactFlow/utils/actions/buildNodeActions';
import { FunnelNodeMetricBadge, AnalyticsNodeCard, FlowLossStub, useFunnelUnit } from '@modules/funnel-insights';
import type { AnalyticsBoundVariable } from '@modules/funnel-insights/types';

/**
 * Spec D D1b analytics-mode frame colors (heat vs funnel median). Raw hexes on
 * purpose — this component styles with literal palette values, not useTheme.
 */
const ANALYTICS_FRAME_COLORS: Record<string, { border: string; glow: string }> = {
  low: { border: '#2e7d32', glow: 'rgba(46, 125, 50, 0.18)' },
  medium: { border: '#ed6c02', glow: 'rgba(237, 108, 2, 0.20)' },
  high: { border: '#d32f2f', glow: 'rgba(211, 47, 47, 0.20)' },
};

// Launch screen indicator text
const LAUNCH_INDICATOR_TEXT = 'First screen';

/**
 * Canvas rework T2 (D-C4): below this zoom the metric banner collapses to the
 * map-LOD — one big heat-colored number per screen, readable from fit view.
 */
const ANALYTICS_MAP_LOD_MAX_ZOOM = 0.35;

/**
 * Node dimensions for free-form containers (v2.19.0).
 * Standard screens keep the fixed 430×932 node byte-identical. Screens with a
 * containerSize render a TRUE-PROPORTION node: width normalized to the
 * standard node width, height follows the container aspect ratio (capped at
 * the standard node height for extreme portrait sizes).
 */
function resolveNodeDimensions(step: unknown): { width: number; height: number; containerSize?: { width: number; height: number } } {
  const containerSize = (step as { containerSize?: { width?: number; height?: number } } | null)?.containerSize;
  if (
    typeof containerSize?.width === 'number' &&
    typeof containerSize?.height === 'number' &&
    containerSize.width > 0 &&
    containerSize.height > 0
  ) {
    const size = { width: containerSize.width, height: containerSize.height };
    let width: number = CANVAS_NODE_DIMENSIONS.WIDTH;
    let height: number = Math.round((width * size.height) / size.width);
    if (height > CANVAS_NODE_DIMENSIONS.HEIGHT) {
      height = CANVAS_NODE_DIMENSIONS.HEIGHT;
      width = Math.round((height * size.width) / size.height);
    }
    return { width, height, containerSize: size };
  }
  return { width: CANVAS_NODE_DIMENSIONS.WIDTH, height: CANVAS_NODE_DIMENSIONS.HEIGHT };
}

export interface FlowOnboardingScreenNodeV2Props extends Omit<NodeProps, 'data'> {
  data: NodeData;
}

const FlowOnboardingScreenNodeV2: React.FC<FlowOnboardingScreenNodeV2Props> = ({ data, selected }) => {
  const { entityId, isLaunchScreen = false, handles = [], analyticsOverlay } = data;
  // Spec D D1b: analytics mode = read-only presentation. undefined → editor
  // rendering, byte-identical to pre-D1b (AC-D1b-7).
  const isAnalyticsMode = data.mode === 'analytics';

  // Canvas locale from context (switchable via CanvasLocaleSelector)
  const { locale } = useCanvasLocale();
  // K3 (D64 gap G3): sample values so @variable tokens render as text, not raw refs
  const { previewVariables } = useCanvasVariables();
  const { showMotivationBadges } = useCanvasBadges();

  // Boolean selector → re-render only when the zoom crosses the LOD threshold,
  // not on every pan/zoom frame (editor mode ignores the value entirely).
  const isMapLod = useStore((state) => state.transform[2] < ANALYTICS_MAP_LOD_MAX_ZOOM);

  // F2 (FT-AC-090): the heat frame follows the page counting unit — the
  // people run has its own median and qualification, so a people-mode node
  // without a people bucket honestly falls back to the dashed neutral frame.
  const funnelUnit = useFunnelUnit();

  // Fetch step from V2 registry only (no V1 fallback)
  const step = v2ScreenRegistry.get(entityId);

  // Derive embedHtmlMap from flexible layout sections for CustomEmbed rendering
  const embedHtmlMap = useMemo(() => {
    const sections = step?.content?.flexibleLayout?.sections ?? [];
    const map: Record<string, string> = {};
    for (const s of sections) {
      const html = s.embedContent?.html;
      if (s.kind === 'CustomEmbed' && html) {
        map[s.id] = html;
      }
    }
    return map;
  }, [step?.content?.flexibleLayout?.sections]);

  // Track render count for debugging
  const renderCount = useRef(0);
  renderCount.current++;

  // Build action list based on step state
  const actions = useMemo(() => {
    if (!step) return [];
    return buildScreenActions(step as any, isLaunchScreen);
  }, [step?.id, isLaunchScreen]);

  // Handle action clicks
  const handleActionClick = useCallback(
    (entityId: string, actionKey: string) => {
      console.log('[FlowOnboardingScreenNodeV2] Action clicked:', { entityId, actionKey });

      if (data.onNodeAction && typeof data.onNodeAction === 'function') {
        data.onNodeAction(entityId, actionKey, 'screen');
      }
    },
    [data.onNodeAction]
  );

  // Early return AFTER all hooks
  if (!step) {
    console.warn(`[FlowOnboardingScreenNodeV2] Step not found in V2 registry: ${entityId}`);
    return (
      <div
        data-testid={`onboarding-node-${entityId}`}
        style={{
          width: 200,
          height: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px dashed red',
          borderRadius: '8px',
          padding: '16px',
        }}
      >
        Screen not found
      </div>
    );
  }

  const nodeDims = resolveNodeDimensions(step);

  // Analytics mode: heat frame vs the funnel median; no-bucket nodes (no
  // traffic / insufficient sample) get a dashed neutral frame — honesty rule.
  const unitHeatBucket = funnelUnit === 'people'
    ? analyticsOverlay?.heatBucketPeople
    : analyticsOverlay?.heatBucket;
  const heatFrame = isAnalyticsMode
    ? ANALYTICS_FRAME_COLORS[unitHeatBucket ?? '']
    : undefined;
  const frameStyle: React.CSSProperties = isAnalyticsMode
    ? {
        border: heatFrame ? `3px solid ${heatFrame.border}` : '3px dashed rgba(0, 0, 0, 0.26)',
        boxShadow: heatFrame ? `0 0 0 3px ${heatFrame.glow}` : '0 2px 8px rgba(0, 0, 0, 0.1)',
      }
    : {
        border: `${selected ? '1px' : '2px'} solid ${selected ? '#1976d2' : '#e0e0e0'}`,
        boxShadow: selected
          ? '0 4px 16px rgba(25, 118, 210, 0.15)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
      };

  return (
    <div
      className={`flow-onboarding-screen-node ${selected ? 'selected' : ''}`}
      data-testid={`onboarding-node-${step.id}`}
      data-screen-type={step.screenType}
      data-screen-name={typeof step.name === 'string' ? step.name : step.id}
      data-is-launch={isLaunchScreen}
      data-entity-type="onboarding-screen-v2"
      data-container-size={nodeDims.containerSize ? `${nodeDims.containerSize.width}x${nodeDims.containerSize.height}` : undefined}
      data-mode={isAnalyticsMode ? 'analytics' : undefined}
      data-heat={isAnalyticsMode ? (unitHeatBucket ?? 'none') : undefined}
      style={{
        width: nodeDims.width,
        height: nodeDims.height,
        ...frameStyle,
        borderRadius: '8px',
        background: 'white',
        position: 'relative',
        overflow: 'visible',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Action Toolbar - shown when node is selected (edit affordance — never in analytics mode) */}
      {!isAnalyticsMode && (
        <NodeToolbar isVisible={selected} position={Position.Top} offset={10}>
          <NodeActionToolbar
            entityId={step.entityId}
            entityName={typeof step.name === 'string' ? step.name : (step.id || 'Unnamed Screen')}
            actions={actions}
            onActionClick={handleActionClick}
          />
        </NodeToolbar>
      )}

      {analyticsOverlay && !isAnalyticsMode && (
        <FunnelNodeMetricBadge metric={analyticsOverlay} />
      )}

      {/* Spec D D1b + canvas rework T2: metric banner ABOVE the node — the
          number leads, the screen preview is context (map/inspect zoom-LOD). */}
      {isAnalyticsMode && analyticsOverlay && (
        <div style={{ position: 'absolute', bottom: '100%', left: -4, right: -4, marginBottom: 10 }}>
          <AnalyticsNodeCard
            metric={analyticsOverlay}
            screenType={step.screenType}
            lod={isMapLod ? 'map' : 'inspect'}
            boundVariables={(data as { boundVariables?: AnalyticsBoundVariable[] }).boundVariables}
          />
        </div>
      )}

      {/* Canvas rework T4 + I-A: the red flow-loss drain under a leaking
          screen — inspect-LOD only (the map-LOD banner number IS the loss).
          With a handler the stub opens the drop-off audience drawer and stops
          propagation; without one clicks bubble to the node wrapper. */}
      {isAnalyticsMode && analyticsOverlay && !isMapLod && (
        <FlowLossStub
          metric={analyticsOverlay}
          onFlowLossClick={(data as { onFlowLossClick?: (screenId: string) => void }).onFlowLossClick}
        />
      )}

      {/* Motivation class badge — GENERATION canvas only (CanvasBadgesContext); the
          regular onboarding canvas stays clean of generation diagnostics */}
      {(() => {
        if (!showMotivationBadges) return null;
        const motivationClass = (step as any).aiMeta?.motivationSpec?.class as string | undefined;
        if (!motivationClass) return null;
        const gain = motivationClass.startsWith('gain');
        const cost = motivationClass.startsWith('cost');
        return (
          <div
            data-testid="motivation-node-class-badge"
            title={`Motivation class: ${motivationClass}`}
            style={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 10,
              fontSize: 10,
              lineHeight: '14px',
              padding: '1px 6px',
              borderRadius: 6,
              background: gain ? '#e6f4ea' : cost ? '#fef7e0' : '#eceff1',
              color: gain ? '#1e7e34' : cost ? '#8a6d00' : '#455a64',
              border: `1px solid ${gain ? '#a5d6b7' : cost ? '#f3d58a' : '#cfd8dc'}`,
              fontWeight: 600,
              pointerEvents: 'none',
            }}
          >
            {motivationClass}
          </div>
        );
      })()}

      {/* Preview - using V2ScreenView from screen-view-v2 package (native V2 rendering) */}
      <div
        data-testid={`onboarding-node-preview-${step.entityId}`}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: '6px',
          pointerEvents: 'none',
          // Analytics mode dims the screen content so metrics win the attention contest
          opacity: isAnalyticsMode ? 0.45 : undefined,
        }}
      >
        {/* The canvas node shows what the visitor gets, including a randomized
            list's shuffled order — seeded deterministically per screen so the
            node does not resample on every render and agrees with the screen
            editor's preview (R-POST-3 / R-W11-5). */}
        <PreviewOptionsRandomizationProvider screenId={step.id ?? entityId}>
        {nodeDims.containerSize ? (
          // True-proportion container node: content renders at the real
          // container size and scales down to fit the node box
          <AutoScaleScreenContainer
            scaleOptions={{
              contentWidth: nodeDims.containerSize.width,
              contentHeight: nodeDims.containerSize.height,
              padding: 0,
            }}
            showBeforeReady
          >
            <div style={{ width: nodeDims.containerSize.width, height: nodeDims.containerSize.height }}>
              <V2ScreenView
                step={step}
                locale={locale}
                previewMode="static"
                embedHtmlMap={embedHtmlMap}
                variables={previewVariables}
              />
            </div>
          </AutoScaleScreenContainer>
        ) : (
          <V2ScreenView
            step={step}
            locale={locale}
            previewMode="static"
            embedHtmlMap={embedHtmlMap}
            variables={previewVariables}
          />
        )}
        </PreviewOptionsRandomizationProvider>
      </div>

      {/* Connection Handles */}
      {/* Input handle (left side) - only if not launch screen */}
      <ConnectionInputHandle entityId={step.entityId} isLaunchScreen={isLaunchScreen} />

      {/* Output handles (right side) */}
      {handles && handles.length > 0 ? (
        handles.map((handle, index) => (
          <ConnectionOutputHandle
            key={handle.id}
            entityId={step.id}
            handle={handle}
            index={index}
          />
        ))
      ) : null}

      {/* Launch screen indicator */}
      {isLaunchScreen && (
        <div
          className="launch-indicator"
          style={{
            position: 'absolute',
            left: '-80px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(25, 118, 210, 0.1)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#1976d2',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {LAUNCH_INDICATOR_TEXT}
        </div>
      )}
    </div>
  );
};

export default FlowOnboardingScreenNodeV2;
